<script setup lang="ts">
import {
  useBrowserStore,
  useCoreStore,
} from '@apps/main/new-desktop/core/stores';

import {
  AppImagesLoader,
  AppLazyLoadComponent,
  AppSvgIcon,
  AppTag,
} from '@libs/ui';

import type { FerryProductCardData, FerryUpsellType } from '../../../models';

import FerryProductCardUpsell from './FerryProductCardUpsell.vue';

interface FerryProductCardContentProps {
  productCardContent: FerryProductCardData;
  printMode: boolean;
}

const props = defineProps<FerryProductCardContentProps>();

interface FerryProductCardContentEmits {
  (event: 'change-upsell', upsellType: FerryUpsellType): void;
  (event: 'show-details'): void;
}

const emit = defineEmits<FerryProductCardContentEmits>();

const browserStore = useBrowserStore();
const coreStore = useCoreStore();

function onContentClick(): void {
  if (browserStore.isSmallDevice) {
    emit('show-details');
  }
}
</script>

<template>
  <div class="ferry-product-card-content" @click="onContentClick">
    <div class="ferry-content">
      <div class="left-section">
        <div class="ferry-logo">
          <AppLazyLoadComponent class="logo">
            <AppImagesLoader
              :key="props.productCardContent.details.logo.urls.join('-')"
              :urls="props.productCardContent.details.logo.urls"
              alt="ferry carrier logo"
              img-type="contain"
              @images-loader-error="coreStore.handleImageLoadError"
            >
              <AppSvgIcon name="ferry" size="24" />
            </AppImagesLoader>
          </AppLazyLoadComponent>
        </div>
        <div class="destinations">
          {{ props.productCardContent.fromPort.country }}
          &mdash;
          {{ props.productCardContent.toPort.country }}
        </div>
      </div>
      <div class="right-section">
        <div class="details">
          <div class="origin">
            {{ props.productCardContent.fromPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.fromPort.country }}
              </span>
            </div>
          </div>

          <div class="time">
            <div class="duration">
              {{ props.productCardContent.details.time }}
            </div>
            <div class="separator">
              <div class="line" />
              <div class="icon-wrapper">
                <AppSvgIcon name="ferry" size="13" color="color-white" />
              </div>
            </div>
            <div class="stops">
              {{ props.productCardContent.details.mainDetails }}
            </div>
          </div>

          <div class="destination">
            {{ props.productCardContent.toPort.time }}
            <div class="port">
              <span class="desktop-port-name">
                {{ props.productCardContent.toPort.country }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="
            browserStore.isSmallDevice &&
            props.productCardContent.upsell.transportationName
          "
          class="transport"
        >
          <span class="transport-name">
            {{ props.productCardContent.upsell.transportationName }}
          </span>
        </div>
        <div
          v-if="props.productCardContent.details.warningTags.length"
          class="warning-tags"
        >
          <AppTag
            v-for="(tag, index) in props.productCardContent.details.warningTags"
            :key="index"
            type="warning"
            :icon="{ name: tag.svgIconType, size: '18' }"
            class="tag-container"
          >
            <template #title>
              <span class="tag-description">
                {{ $t(tag.description) }}
              </span>
            </template>
          </AppTag>
        </div>
        <div class="upsell-item">
          <FerryProductCardUpsell
            :vehicles="props.productCardContent.upsell.vehicles"
            :ticket="props.productCardContent.upsell.ticket"
            :cabin="props.productCardContent.upsell.cabin"
            :token="props.productCardContent.upsell.token"
            :ingredient-id="props.productCardContent.upsell.ingredientId"
            :allow-vehicles="props.productCardContent.upsell.allowVehicles"
            :transportation-name="
              props.productCardContent.upsell.transportationName
            "
            :print-mode="props.printMode"
            @change-upsell="emit('change-upsell', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ferry-product-card-content {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto;

  @include for-small-devices-max {
    grid-template-rows: auto auto;
    grid-template-columns: auto;
  }

  .warning-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-4);
    justify-content: center;
    padding-bottom: var(--spacing-12);

    .tag-description {
      @include font-label-s-light;
    }
  }

  .ferry-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: fit-content;
    flex-wrap: wrap;

    @include for-small-devices-max {
      border-right: unset;
    }

    .left-section {
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: var(--spacing-8);
      padding: var(--spacing-16) var(--spacing-16) 0 var(--spacing-16);
      @include for-small-devices-max {
        padding: 0;
        padding-top: var(--spacing-16);
        align-items: center;
      }
      .ferry-logo {
        height: 30px;
        box-sizing: content-box;

        @include for-small-devices-max {
          padding: 0;
        }
      }
      .destinations {
        display: none;
        @include font-label-s-light;
        @include for-small-devices-max {
          display: flex;
          color: var(--color-secondary-60);
        }
      }
    }
    .right-section {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);

      @include for-small-devices-max {
        padding: var(--spacing-16) 0 0;
      }

      .details {
        --mid-block-width: 100px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-16);
        width: 100%;
        padding-bottom: var(--spacing-12);
        @include for-small-devices-max {
          padding: 0;
        }

        .origin,
        .destination {
          display: flex;
          flex-direction: column;
          // align origin and destination columns mid block width
          flex-basis: calc(50%);
          gap: var(--spacing-4);
          font: var(--font-label-xxl-bold);
          span {
            @include font-label-l-light;
          }

          .port {
            color: var(--color-secondary-60);
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: var(--spacing-4);
            .desktop-port-name {
              display: block;
              @include for-small-devices-max {
                display: none;
              }
            }
          }
        }
        .origin {
          align-items: flex-end;
          .desktop-port-name {
            text-align: end;
          }
          .port {
            justify-content: end;
          }
        }
        .destination {
          align-items: flex-start;
        }

        .time {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100px;

          .duration {
            @include font-label-s-light;
            color: var(--color-secondary-60);
            padding-bottom: var(--spacing-12);
          }
          .separator {
            position: relative;
            width: 100%;
            height: var(--spacing-24);

            .line {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100%;
              height: 1px;
              background: rgb(255, 255, 255);
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(36, 30, 61, 1) 50%,
                rgba(255, 255, 255, 1) 100%
              );
            }
            .icon-wrapper {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: var(--color-primary-5);
              border-radius: var(--border-radius-20);
              padding: var(--spacing-4);
            }
          }
          .stops {
            color: var(--color-secondary-60);
            @include font-label-s-light;
            text-align: center;
            white-space: nowrap;
          }
        }
      }
      .transport {
        padding-bottom: var(--spacing-24);
        .transport-name {
          @include font-label-s-light;
          color: var(--color-secondary-60);
        }
      }

      .upsell-item {
        @include for-small-devices-max {
          padding: var(--spacing-12);
          width: 100%;
          border-top: 1px solid var(--color-secondary-93);
        }
      }
    }
  }

  .ferry-upsell {
    border-top: 1px solid var(--color-secondary-93);
  }
}
</style>
